import { Injectable } from '@angular/core';

/**
 * BrowserFingerprintService
 *
 * Generates unique ID for anonymous users based on browser characteristics.
 * Stores fingerprint in localStorage for persistence between sessions.
 *
 * Used for:
 * - Identifying anonymous users in analytics
 * - Preventing duplicate views
 * - Basic uniqueness without cookies
 */
@Injectable({
  providedIn: 'root'
})
export class BrowserFingerprintService {
  private readonly STORAGE_KEY = '3dway_viewer_id';
  private cachedFingerprint: string | null = null;

  constructor() {}

  /**
   * Get or create browser fingerprint for anonymous users
   * Persisted in localStorage between sessions
   *
   * @returns fingerprint in format "anon_xxxxxxxxxxxx"
   */
  getFingerprint(): string {
    // Memory cache (to avoid parsing localStorage every time)
    if (this.cachedFingerprint) {
      return this.cachedFingerprint;
    }

    // Check localStorage
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        this.cachedFingerprint = stored;
        console.log('[Fingerprint] Loaded from localStorage:', stored);
        return stored;
      }
    } catch (e) {
      // localStorage unavailable (Safari private mode, etc.)
      console.warn('[Fingerprint] localStorage unavailable, generating session-only ID');
      return this.generateSessionFingerprint();
    }

    // Generate new fingerprint
    const fingerprint = this.generateFingerprint();

    try {
      localStorage.setItem(this.STORAGE_KEY, fingerprint);
      console.log('[Fingerprint] Created and saved:', fingerprint);
    } catch (e) {
      console.warn('[Fingerprint] Failed to save to localStorage');
    }

    this.cachedFingerprint = fingerprint;
    return fingerprint;
  }

  private generateFingerprint(): string {
    const components = [
      navigator.userAgent,
      screen.width + 'x' + screen.height + 'x' + screen.colorDepth,
      new Date().getTimezoneOffset().toString(),
      navigator.language,
      navigator.platform,
      // Canvas fingerprint can be added for more uniqueness (optional)
      // this.getCanvasFingerprint()
    ];

    const raw = components.join('|');
    const hash = this.simpleHash(raw);

    return `anon_${hash}`;
  }

  /**
   * Session-only fingerprint (if localStorage unavailable)
   * Valid only for current browser session
   *
   * @private
   * @returns temporary fingerprint
   */
  private generateSessionFingerprint(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    return `anon_session_${timestamp}_${random}`;
  }

  /**
   * Simple hash for fingerprint (without crypto dependencies)
   * Uses basic DJB2 algorithm
   *
   * @private
   * @param str - string to hash
   * @returns hex string (12 characters)
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      // eslint-disable-next-line no-bitwise
      hash = ((hash << 5) - hash) + char;
      // eslint-disable-next-line no-bitwise
      hash = hash & hash; // Convert to 32bit integer
    }

    // Convert to hex and take first 12 characters
    const hex = Math.abs(hash).toString(16);
    return hex.substring(0, 12).padStart(12, '0');
  }

  /**
   * Clear fingerprint (for testing or logout)
   * After calling this method, getFingerprint() will generate new ID
   *
   * @public
   */
  clearFingerprint(): void {
    this.cachedFingerprint = null;
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      console.log('[Fingerprint] Cleared');
    } catch (e) {
      console.warn('[Fingerprint] Failed to clear localStorage');
    }
  }

  /**
   * Optional method for more complex Canvas fingerprinting
   * Can be uncommented if more uniqueness is needed
   *
   * @private
   * @returns canvas hash
   */
  // private getCanvasFingerprint(): string {
  //   try {
  //     const canvas = document.createElement('canvas');
  //     const ctx = canvas.getContext('2d');
  //     if (!ctx) return '';
  //
  //     ctx.textBaseline = 'top';
  //     ctx.font = '14px Arial';
  //     ctx.fillStyle = '#f60';
  //     ctx.fillRect(125, 1, 62, 20);
  //     ctx.fillStyle = '#069';
  //     ctx.fillText('3DWay Analytics', 2, 15);
  //
  //     return canvas.toDataURL();
  //   } catch (e) {
  //     return '';
  //   }
  // }
}
